import "bootstrap/dist/css/bootstrap.css";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { APP_VERSION } from "./components/Version";

// MSAL imports
import {
    PublicClientApplication,
    EventType,
} from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

/* ---------------------------------------------------------
   Cookie helpers
--------------------------------------------------------- */
function getCookie(name) {
    const match = document.cookie.match(
        new RegExp(
            "(^|;\\s*)" +
            name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&") +
            "=([^;]*)"
        )
    );
    return match ? decodeURIComponent(match[2]) : null;
}

function hardenCookieSameSite(name, sameSite = "Lax") {
    const value = getCookie(name) ?? "";
    document.cookie = `${name}=${encodeURIComponent(
        value
    )}; Path=/; SameSite=${sameSite}; Secure`;
}

// Set SameSite for MSAL encryption cookie BEFORE MSAL initializes
hardenCookieSameSite("msal.cache.encryption", "Lax");

/* ---------------------------------------------------------
   MSAL initialization
--------------------------------------------------------- */
export const msalInstance = new PublicClientApplication(msalConfig);

/* ---------------------------------------------------------
   Version check
--------------------------------------------------------- */
const storedVersion = localStorage.getItem("app_version");
if (storedVersion !== APP_VERSION) {
    Object.keys(localStorage)
        .filter((key) => key.startsWith("msal."))
        .forEach((key) => localStorage.removeItem(key));

    localStorage.setItem("app_version", APP_VERSION);
    window.location.replace(window.location.href);
}

/* ---------------------------------------------------------
   Account handling
--------------------------------------------------------- */
if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

msalInstance.enableAccountStorageEvents();

msalInstance.addEventCallback((event) => {
    if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload?.account
    ) {
        msalInstance.setActiveAccount(event.payload.account);
    }
});

/* ---------------------------------------------------------
   App bootstrap
--------------------------------------------------------- */
function startApp() {
    const baseUrl =
        document
            .getElementsByTagName("base")[0]
            ?.getAttribute("href") ?? "/";

    const rootElement = document.getElementById("root");
    const root = createRoot(rootElement);

    root.render(
        <BrowserRouter basename={baseUrl}>
            <App pca={msalInstance} />
        </BrowserRouter>
    );
}

/* ---------------------------------------------------------
   WICHTIG: handleRedirectPromise vor dem Rendering aufrufen, damit Redirect-Logins korrekt verarbeitet werden
--------------------------------------------------------- */

export const authState = {
    redirectCompleted: false,
};

msalInstance
    .handleRedirectPromise()
    .then((response) => {
        if (response?.account) {
            msalInstance.setActiveAccount(response.account);
        }
        authState.redirectCompleted = true;
        startApp();
    })
    .catch((error) => {
        console.error("MSAL redirect handling failed:", error);
        authState.redirectCompleted = true;
        startApp(); // Fallback, damit App nicht hängen bleibt
    });

/* ---------------------------------------------------------
   Service worker & metrics
--------------------------------------------------------- */
serviceWorkerRegistration.unregister();
reportWebVitals();